import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    TextField,
    Alert,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tooltip,
    Badge,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import {
    Block as BlockIcon,
    LockOpen as UnblockIcon,
    Add as AddIcon,
    Refresh as RefreshIcon,
    Download as DownloadIcon,
    Security as SecurityIcon,
} from '@mui/icons-material';
import IPBlockingService, { BlockedIP, SuspiciousActivity, BlockingStats } from '../services/ipBlockingService';
import IPEnforcementService from '../services/ipEnforcementService';

const SimpleIPBlockingManager: React.FC = () => {
    const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
    const [suspiciousActivities, setSuspiciousActivities] = useState<SuspiciousActivity[]>([]);
    const [stats, setStats] = useState<BlockingStats | null>(null);
    const [currentUserIP, setCurrentUserIP] = useState<string | null>(null);
    const [newIP, setNewIP] = useState('');
    const [newReason, setNewReason] = useState('');
    const [newSeverity, setNewSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
    const [blockDialogOpen, setBlockDialogOpen] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Load data from services
    const loadData = useCallback(async () => {
        try {
            const [blocked, suspicious, statistics] = await Promise.all([
                IPBlockingService.getBlockedIPs(),
                IPBlockingService.getSuspiciousActivities(),
                IPBlockingService.getBlockingStats()
            ]);

            setBlockedIPs(blocked);
            setSuspiciousActivities(suspicious);
            setStats(statistics);
            setError(null);
        } catch (err) {
            console.error('Failed to load IP blocking data:', err);
            setError('Failed to load data');
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Refresh data
    const handleRefresh = useCallback(async () => {
        setIsRefreshing(true);
        await loadData();
        setIsRefreshing(false);
    }, [loadData]);

    // Load data on mount and set up auto-refresh
    useEffect(() => {
        loadData();
        
        // Get current user's IP
        const userIP = IPEnforcementService.getCurrentIP();
        setCurrentUserIP(userIP);
        
        const interval = setInterval(() => {
            loadData();
            // Update user IP if it changed
            const updatedIP = IPEnforcementService.getCurrentIP();
            if (updatedIP !== currentUserIP) {
                setCurrentUserIP(updatedIP);
            }
        }, 15000); // Refresh every 15 seconds
        
        return () => clearInterval(interval);
    }, [loadData, currentUserIP]);

    const handleBlockIP = async () => {
        if (!newIP || !newReason) {
            setError('Please enter both IP address and reason');
            return;
        }

        // Validate IP format
        const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipRegex.test(newIP)) {
            setError('Please enter a valid IP address (e.g., 192.168.1.1)');
            return;
        }

        try {
            const result = await IPBlockingService.blockIP(newIP, newReason, newSeverity, 'admin');
            
            if (result.success) {
                setSuccess(`IP ${newIP} blocked successfully`);
                setNewIP('');
                setNewReason('');
                setNewSeverity('medium');
                setBlockDialogOpen(false);
                setError(null);
                await loadData(); // Reload data
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError('Failed to block IP');
        }
    };

    const handleUnblockIP = async (ip: string) => {
        try {
            const result = await IPBlockingService.unblockIP(ip);
            
            if (result.success) {
                setSuccess(`IP ${ip} unblocked successfully`);
                await loadData(); // Reload data
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError('Failed to unblock IP');
        }
    };

    const handleBlockSuspiciousIP = async (ip: string, reason: string) => {
        try {
            const result = await IPBlockingService.blockIP(ip, reason, 'high', 'admin');
            
            if (result.success) {
                setSuccess(`Suspicious IP ${ip} blocked successfully`);
                await loadData();
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError('Failed to block suspicious IP');
        }
    };

    const exportData = useCallback((format: 'json' | 'csv') => {
        const data = format === 'json'
            ? JSON.stringify({ blockedIPs, suspiciousActivities, stats }, null, 2)
            : blockedIPs.map(ip => `${ip.ip},${ip.reason},${ip.blockedAt.toISOString()},${ip.blockedBy}`).join('\n');
        
        const blob = new Blob([data], { type: format === 'json' ? 'application/json' : 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `blocked-ips.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }, [blockedIPs, suspiciousActivities, stats]);

    const getSeverityColor = (reason: string) => {
        if (reason.toLowerCase().includes('critical') || reason.toLowerCase().includes('attack')) {
            return 'error';
        }
        if (reason.toLowerCase().includes('suspicious') || reason.toLowerCase().includes('spam')) {
            return 'warning';
        }
        return 'default';
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                    IP Blocking Manager
                </Typography>
                <Box display="flex" gap={1}>
                    <Tooltip title="Refresh">
                        <IconButton onClick={handleRefresh} disabled={isRefreshing}>
                            {isRefreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export JSON">
                        <IconButton onClick={() => exportData('json')}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setBlockDialogOpen(true)}
                        sx={{ borderRadius: 2 }}
                    >
                        Block IP
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Current User IP Info */}
            {currentUserIP && (
                <Alert severity="info" sx={{ mb: 2 }} icon={<SecurityIcon />}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">
                            Your IP Address: 
                        </Typography>
                        <Chip 
                            label={currentUserIP} 
                            size="small" 
                            sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                            (Client-side blocking is active - blocks will take effect immediately)
                        </Typography>
                    </Box>
                </Alert>
            )}

            {/* Statistics Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Total Blocked</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'error.main' }}>
                                {stats?.totalBlocked || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Active Blocks</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                {stats?.activeBlocks || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Suspicious IPs</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'info.main' }}>
                                {stats?.suspiciousActivities || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Auto-Blocked</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 600, color: 'success.main' }}>
                                {stats?.autoBlocked || 0}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Blocked IPs Table */}
            <Card variant="outlined" sx={{ borderRadius: 2, mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" mb={2}>
                        Blocked IPs ({blockedIPs.length})
                    </Typography>

                    {blockedIPs.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No IPs are currently blocked
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>IP Address</TableCell>
                                        <TableCell>Reason</TableCell>
                                        <TableCell>Blocked At</TableCell>
                                        <TableCell>Blocked By</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {blockedIPs.map((blocked) => (
                                        <TableRow key={blocked.id}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                                    {blocked.ip}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Typography variant="body2">{blocked.reason}</Typography>
                                                    <Chip
                                                        label={getSeverityColor(blocked.reason)}
                                                        size="small"
                                                        color={getSeverityColor(blocked.reason) as any}
                                                    />
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {blocked.blockedAt.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={blocked.blockedBy} size="small" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Unblock IP">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleUnblockIP(blocked.ip)}
                                                        color="success"
                                                    >
                                                        <UnblockIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>

            {/* Suspicious Activities */}
            {suspiciousActivities.length > 0 && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6">
                                <Badge badgeContent={suspiciousActivities.length} color="warning">
                                    Suspicious Activities
                                </Badge>
                            </Typography>
                        </Box>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>IP Address</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Details</TableCell>
                                        <TableCell>Timestamp</TableCell>
                                        <TableCell>Severity</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {suspiciousActivities.map((activity) => (
                                        <TableRow key={activity.id}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                                    {activity.ip}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={activity.type} size="small" color="warning" />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{activity.details}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {activity.timestamp.toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={activity.severity} 
                                                    size="small" 
                                                    color={activity.severity === 'critical' ? 'error' : 'warning'}
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Block this IP">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleBlockSuspiciousIP(activity.ip, `Suspicious: ${activity.type}`)}
                                                        color="error"
                                                    >
                                                        <BlockIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            <Dialog open={blockDialogOpen} onClose={() => setBlockDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Block IP Address</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2 }}>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="IP Address"
                            fullWidth
                            variant="outlined"
                            value={newIP}
                            onChange={(e) => setNewIP(e.target.value)}
                            placeholder="192.168.1.1"
                            sx={{ mb: 2 }}
                            helperText="Enter a valid IPv4 address"
                        />
                        <TextField
                            margin="dense"
                            label="Reason"
                            fullWidth
                            variant="outlined"
                            multiline
                            rows={3}
                            value={newReason}
                            onChange={(e) => setNewReason(e.target.value)}
                            placeholder="Suspicious activity detected"
                            sx={{ mb: 2 }}
                        />
                        <FormControl fullWidth>
                            <InputLabel>Severity</InputLabel>
                            <Select
                                value={newSeverity}
                                label="Severity"
                                onChange={(e) => setNewSeverity(e.target.value as 'low' | 'medium' | 'high' | 'critical')}
                            >
                                <MenuItem value="low">Low</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="high">High</MenuItem>
                                <MenuItem value="critical">Critical</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBlockDialogOpen(false)}>
                        Cancel
                    </Button>
                    <Button onClick={handleBlockIP} variant="contained" color="error" startIcon={<BlockIcon />}>
                        Block IP
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SimpleIPBlockingManager;

